import React, { useMemo } from "react";

const Item = ({ icon, title, value, color = "text-white" }) => (
  <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-800 border border-gray-700">
    <div className="text-2xl">{icon}</div>

    <div>
      <div className="text-xs uppercase text-gray-400">{title}</div>

      <div className={`font-bold text-lg ${color}`}>{value}</div>
    </div>
  </div>
);

export default function ScorePlusInsights({ records = [] }) {
  const insights = useMemo(() => {
    if (!records.length) {
      return null;
    }

    const activityMap = {};
    const durationMap = {};
    const scoreMap = {};

    let moodTotal = 0;
    let energyTotal = 0;
    let durationTotal = 0;

    records.forEach((r) => {
      const act = r.act || "Others";

      activityMap[act] = (activityMap[act] || 0) + 1;

      durationMap[act] = (durationMap[act] || 0) + (Number(r.duration) || 0);

      scoreMap[act] = (scoreMap[act] || 0) + (Number(r.score) || 0);

      moodTotal += Number(r.mood) || 0;
      energyTotal += Number(r.energy) || 0;
      durationTotal += Number(r.duration) || 0;
    });

    const bestActivity = Object.keys(scoreMap).sort(
      (a, b) => scoreMap[b] - scoreMap[a],
    )[0];

    const longestActivity = Object.keys(durationMap).sort(
      (a, b) => durationMap[b] - durationMap[a],
    )[0];

    const mostFrequent = Object.keys(activityMap).sort(
      (a, b) => activityMap[b] - activityMap[a],
    )[0];

    const averageMood = (moodTotal / records.length).toFixed(1);

    const averageEnergy = (energyTotal / records.length).toFixed(1);

    const averageHours = (
      durationTotal /
      60 /
      new Set(records.map((r) => r.date)).size
    ).toFixed(1);

    return {
      bestActivity,

      longestActivity,

      mostFrequent,

      averageMood,

      averageEnergy,

      averageHours,
    };
  }, [records]);

  if (!insights) return null;

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-xl p-5">
      <h2 className="text-xl font-bold text-white mb-5">
        Productivity Insights
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <Item
          icon="🏆"
          title="Best Activity"
          value={insights.bestActivity}
          color="text-green-400"
        />

        <Item
          icon="⭐"
          title="Most Frequent"
          value={insights.mostFrequent}
          color="text-cyan-400"
        />

        <Item
          icon="⏱"
          title="Longest Time Spent"
          value={insights.longestActivity}
          color="text-yellow-400"
        />

        <Item
          icon="😊"
          title="Average Mood"
          value={insights.averageMood}
          color="text-pink-400"
        />

        <Item
          icon="⚡"
          title="Average Energy"
          value={insights.averageEnergy}
          color="text-orange-400"
        />

        <Item
          icon="⌛"
          title="Daily Average Hours"
          value={`${insights.averageHours} hrs`}
          color="text-purple-400"
        />
      </div>
    </div>
  );
}
